import React, { useEffect, useRef, useState } from "react";
import { Trash2, Mic2, Clock, Upload, Download, CheckCircle2, Circle, Copy, ExternalLink, Loader2, Sparkles, Radio, Image as ImageIcon, ZoomIn } from "lucide-react";
import ImageSwapLightbox from "@/components/ImageSwapLightbox";
import PageHeader from "@/components/PageHeader";
import ImageCropModal from "@/components/ImageCropModal";
import { api, APP_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function PublishPanel({ stationId, onRefresh }) {
  const [feed, setFeed] = useState(null);
  const [ready, setReady] = useState(null);

  const load = async () => {
    if (!stationId) return;
    try {
      const [f, r] = await Promise.all([
        api.get(`/podcast/feed-url/${stationId}`),
        api.get(`/podcast/feed-readiness/${stationId}`),
      ]);
      setFeed(f.data);
      setReady(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stationId, onRefresh]);

  if (!stationId) return null;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const checklist = ready ? [
    { ok: ready.station_name_ok, label: "Station / show name set" },
    { ok: ready.cover_art_ok, label: "Cover art (station logo) uploaded" },
    { ok: ready.category_ok, label: "Genre / category set" },
    { ok: ready.station_description_ok, label: "Station description present" },
    { ok: ready.has_publishable_episode, label: `At least 1 published episode (${ready.published_count}/${ready.episode_count})` },
  ] : [];

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-6 mb-8" data-testid="publish-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-brand-amber" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber">// PUBLISH EVERYWHERE</div>
        </div>
        {ready && (
          <div className={`font-mono text-[10px] uppercase tracking-wider ${ready.ready_to_submit ? "text-emerald-400" : "text-ink-muted"}`} data-testid="publish-ready-badge">
            {ready.ready_to_submit ? "Ready to submit" : "Setup required"}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-[1.4fr_1fr] gap-6">
        {/* Left: feed URL + submit buttons */}
        <div className="space-y-4">
          <div>
            <div className="text-xs text-ink-muted mb-1">Your podcast RSS feed</div>
            <div className="flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-3 py-2">
              <code className="flex-1 truncate text-xs font-mono text-ink-primary" data-testid="rss-feed-url">
                {feed?.feed_url || "…"}
              </code>
              <button onClick={() => copy(feed?.feed_url, "RSS feed URL")} className="text-ink-muted hover:text-cyan-300 transition" data-testid="copy-feed-url">
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-1 text-[10px] text-ink-muted">
              Submit this URL once per platform. They&apos;ll auto-discover new episodes from then on.
            </div>
          </div>

          {/* Tier 1 — major streaming destinations */}
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-ink-muted mb-2">// Major streaming destinations</div>
            <div className="grid grid-cols-2 gap-2" data-testid="podcast-platforms-major">
              {(feed?.platforms || []).filter(p => p.tier === "major").map((p) => (
                <PlatformTile key={p.id} platform={p} />
              ))}
            </div>
          </div>

          {/* Tier 2 — indie + auto-discovery apps */}
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-ink-muted mb-2">
              // Indie apps &amp; auto-discovery
              <span className="ml-2 normal-case tracking-normal text-ink-muted/70">
                ★ = auto-discovers from Apple
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="podcast-platforms-indie">
              {(feed?.platforms || []).filter(p => p.tier === "indie").map((p) => (
                <PlatformTile key={p.id} platform={p} />
              ))}
            </div>
          </div>
        </div>

        {/* Right: readiness checklist */}
        <div>
          <div className="text-xs text-ink-muted mb-2">Feed readiness</div>
          <ul className="space-y-1.5" data-testid="readiness-list">
            {checklist.map((c, i) => (
              <li key={i} className="flex items-center gap-2 text-xs">
                {c.ok
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                  : <Circle className="h-3.5 w-3.5 text-ink-muted shrink-0" />
                }
                <span className={c.ok ? "text-ink-primary" : "text-ink-muted"}>{c.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SubmitLink({ href, label, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
       className="group flex items-center justify-between rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:border-brand-amber/50 hover:bg-bg-surface transition">
      <span className="truncate">{label}</span>
      <ExternalLink className="h-3 w-3 text-ink-muted group-hover:text-cyan-300 transition" />
    </a>
  );
}

// Branded platform tile — uses the platform's brand color for the left swatch
// + hover ring. Shows a small ★ when the app auto-discovers from Apple.
function PlatformTile({ platform }) {
  return (
    <a
      href={platform.submit_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`submit-${platform.id}`}
      title={platform.hint || `Open ${platform.label}`}
      className="group relative flex items-center gap-2.5 rounded-md border border-line bg-bg-elevated pl-2 pr-3 py-2 text-xs hover:bg-bg-surface transition"
      style={{ "--p": platform.color }}
    >
      {/* Brand-colored left swatch */}
      <span
        className="h-7 w-1.5 rounded-full shrink-0 transition-all group-hover:w-2"
        style={{ background: platform.color, boxShadow: `0 0 12px ${platform.color}55` }}
      />
      <span className="min-w-0 flex-1 truncate text-ink-primary group-hover:text-cyan-200">
        {platform.label}
      </span>
      {platform.auto_discover && (
        <span title="Auto-discovers from Apple Podcasts directory"
              className="font-mono text-[9px] tracking-wider text-amber-300/80 shrink-0">★</span>
      )}
      <ExternalLink className="h-3 w-3 text-ink-muted group-hover:text-cyan-300 transition shrink-0" />
    </a>
  );
}

function EpisodeCard({ ep, onChange, onDelete }) {
  const [uploading, setUploading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [launchPack, setLaunchPack] = useState(null);
  const [packLoading, setPackLoading] = useState(false);
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverStyle, setCoverStyle] = useState("ai");
  const [cropFile, setCropFile] = useState(null);
  const [showCover, setShowCover] = useState(false);
  const coverFileRef = useRef(null);

  const uploadCover = async (blob) => {
    setCoverLoading(true);
    try {
      const form = new FormData();
      form.append("file", blob, "cover.jpg");
      await api.post(`/podcasts/${ep.episode_id}/cover-art/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover uploaded");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cover upload failed");
    } finally {
      setCoverLoading(false);
      if (coverFileRef.current) coverFileRef.current.value = "";
    }
  };
  const fileRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/podcasts/${ep.episode_id}/audio`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio uploaded");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const togglePublished = async (value) => {
    setToggling(true);
    try {
      await api.patch(`/podcasts/${ep.episode_id}`, { published: value });
      toast.success(value ? "Episode is now live in the feed" : "Episode unpublished");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setToggling(false); }
  };

  const genPack = async () => {
    setPackLoading(true);
    try {
      const res = await api.post(`/podcasts/${ep.episode_id}/launch-pack`);
      setLaunchPack(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate pack");
    } finally { setPackLoading(false); }
  };

  const genCover = async () => {
    setCoverLoading(true);
    try {
      await api.post(`/podcasts/${ep.episode_id}/cover-art`, { style: coverStyle });
      toast.success(coverStyle === "ai" ? "AI cover art generated"
        : coverStyle === "branded_card" ? "Branded cover card generated"
        : "Unsplash photo attached — credit added to feed");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cover generation failed");
    } finally { setCoverLoading(false); }
  };

  const title = (ep.title || "").split("\n")[0].replace(/^[0-9.)\-\s]+/, "");
  const hasAudio = !!ep.audio_storage_path;

  const downloadAudio = () => {
    // Browser-native download: streams straight to the download manager
    // (no 14MB+ blob buffering in JS, survives slow connections).
    // Session cookie carries the auth automatically.
    const a = document.createElement("a");
    a.href = `${APP_ORIGIN}/api/podcast/creator/episodes/${ep.episode_id}/audio?download=1`;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-6 lift" data-testid={`ep-${ep.episode_id}`}>
      <div className="flex items-start gap-4">
        {/* Cover thumbnail — click to enlarge */}
        <div className="shrink-0">
          {ep.image_url ? (
            <button
              type="button"
              onClick={() => setShowCover(true)}
              className="group relative block cursor-zoom-in rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-amber/60"
              data-testid={`ep-cover-zoom-${ep.episode_id}`}
              title="Click to enlarge"
            >
              <img
                src={ep.image_url}
                alt="cover"
                className="h-20 w-20 rounded-lg object-cover border border-line transition group-hover:border-brand-amber/60"
                data-testid={`ep-cover-${ep.episode_id}`}
              />
              <span className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/40 transition grid place-items-center">
                <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition" />
              </span>
            </button>
          ) : (
            <div className="h-20 w-20 rounded-lg border border-dashed border-line bg-bg-elevated grid place-items-center">
              <ImageIcon className="h-5 w-5 text-ink-muted" aria-hidden="true" />
            </div>
          )}
        </div>

        {showCover && ep.image_url && (
          <ImageSwapLightbox
            imageUrl={ep.image_url}
            title={title}
            creditText={ep.image_credit?.photographer ? `photo: ${ep.image_credit.photographer} / Unsplash` : "Podcast cover art"}
            credit={ep.image_credit?.photographer ? ep.image_credit : null}
            altsUrl={`/podcasts/${ep.episode_id}/cover-art/alternatives`}
            selectUrl={`/podcasts/${ep.episode_id}/cover-art/select`}
            unsplashOptionsUrl={`/podcasts/${ep.episode_id}/cover-art/unsplash-options`}
            unsplashSelectUrl={`/podcasts/${ep.episode_id}/cover-art/unsplash-select`}
            onClose={() => setShowCover(false)}
            onSelected={() => onChange()}
            testIdBase={`cover-${ep.episode_id}`}
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-bold mb-2 line-clamp-2">{title}</div>
          <div className="flex items-center gap-3 text-xs text-ink-muted font-mono mb-3 flex-wrap">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />
              {ep.duration_seconds ? `${Math.round(ep.duration_seconds/60)}min` : `${ep.duration_minutes || 30}min`}
            </span>
            <span>{new Date(ep.created_at).toLocaleDateString()}</span>
            {hasAudio && <span className="text-emerald-400">● audio attached</span>}
            {ep.image_url && <span className="text-violet-400">● cover art</span>}
            {ep.image_credit?.photographer && (
              <span className="text-sky-400" data-testid={`ep-cover-credit-${ep.episode_id}`}>
                photo:{" "}
                <a href={ep.image_credit.photographer_url} target="_blank" rel="noreferrer" className="underline hover:text-sky-300">{ep.image_credit.photographer}</a>
                {" "}/{" "}
                <a href={ep.image_credit.unsplash_url || "https://unsplash.com/?utm_source=broadcastflow_ai&utm_medium=referral"} target="_blank" rel="noreferrer" className="underline hover:text-sky-300">Unsplash</a>
              </span>
            )}
            {ep.published && <span className="text-brand-amber">● LIVE in feed</span>}
          </div>
          {ep.summary && <p className="text-sm text-ink-secondary mb-3 line-clamp-2">{ep.summary}</p>}
        </div>
        <button onClick={() => onDelete(ep.episode_id)} className="text-ink-muted hover:text-brand-onair transition shrink-0"
                data-testid={`ep-delete-${ep.episode_id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      </div>

      {/* Publish controls row */}
      <div className="mt-4 grid sm:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-center rounded-lg border border-line bg-bg-elevated px-4 py-3">
        <input ref={fileRef} type="file" accept="audio/*" className="hidden"
               onChange={(e) => handleUpload(e.target.files?.[0])}
               data-testid={`ep-audio-input-${ep.episode_id}`} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid={`ep-upload-btn-${ep.episode_id}`}
                className="inline-flex items-center gap-2 text-xs text-ink-secondary hover:text-cyan-300 transition">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {hasAudio ? (uploading ? "Uploading…" : `Replace audio (${Math.round((ep.audio_size_bytes||0)/1024/1024)} MB)`) : (uploading ? "Uploading…" : "Upload audio (MP3/M4A)")}
        </button>
        {hasAudio && (
          <button onClick={downloadAudio}
                  data-testid={`ep-download-btn-${ep.episode_id}`}
                  title="Download the episode MP3"
                  className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 transition">
            <Download className="h-3.5 w-3.5" />
            Download MP3
          </button>
        )}
        <div className="flex items-center gap-1.5">
          <select value={coverStyle} onChange={(e) => setCoverStyle(e.target.value)}
                  data-testid={`ep-cover-style-${ep.episode_id}`}
                  className="rounded-md border border-line bg-bg px-2 py-1.5 text-[11px] text-ink-secondary focus:outline-none focus:border-violet-500/60">
            <option value="ai">AI painted</option>
            <option value="branded_card">Branded card</option>
            <option value="unsplash_photo">Unsplash photo</option>
          </select>
          <button onClick={genCover} disabled={coverLoading}
                  data-testid={`ep-cover-btn-${ep.episode_id}`}
                  title="Generate per-episode cover art"
                  className="inline-flex items-center gap-2 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs text-violet-300 hover:bg-violet-500/20 disabled:opacity-50 transition">
            {coverLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
            {coverLoading ? "Creating…" : ep.image_url ? "Re-gen cover" : "Cover art"}
          </button>
          <input ref={coverFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropFile(f); }}
                 data-testid={`ep-cover-upload-input-${ep.episode_id}`} />
          <button onClick={() => coverFileRef.current?.click()} disabled={coverLoading}
                  data-testid={`ep-cover-upload-btn-${ep.episode_id}`}
                  title="Upload your own cover (PNG/JPG/WEBP, 10 MB max)"
                  className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5 text-xs text-ink-secondary hover:text-ink-primary hover:border-violet-500/50 disabled:opacity-50 transition">
            <Upload className="h-3.5 w-3.5" />
          </button>
          {cropFile && (
            <ImageCropModal file={cropFile} aspect={1} maxWidth={1400} title="Frame episode cover"
              onCancel={() => { setCropFile(null); if (coverFileRef.current) coverFileRef.current.value = ""; }}
              onCropped={(blob) => { setCropFile(null); uploadCover(blob); }} />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Published</span>
          <Switch
            checked={!!ep.published}
            disabled={!hasAudio || toggling}
            onCheckedChange={togglePublished}
            data-testid={`ep-publish-toggle-${ep.episode_id}`}
          />
        </div>
        <button onClick={genPack} disabled={!ep.published || packLoading}
                data-testid={`ep-launch-pack-${ep.episode_id}`}
                className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-3 py-1.5 text-xs font-semibold text-black disabled:opacity-40 hover:bg-brand-amber/90 transition">
          {packLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Launch pack
        </button>
      </div>

      <details className="mt-4 group">
        <summary className="cursor-pointer text-xs font-mono uppercase tracking-wider text-brand-amber list-none">▸ View full package</summary>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {ep.description && <Block title="Description" body={ep.description} />}
          {ep.show_notes && <Block title="Show notes" body={ep.show_notes} />}
          {ep.seo_keywords && <Block title="SEO keywords" body={ep.seo_keywords} />}
          {ep.hashtags && <Block title="Hashtags" body={ep.hashtags} />}
        </div>
      </details>

      <Dialog open={!!launchPack} onOpenChange={(o) => !o && setLaunchPack(null)}>
        <DialogContent className="max-w-2xl bg-bg-surface border-line" data-testid="launch-pack-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Launch pack — {title}</DialogTitle>
            <DialogDescription>One-click captions across 6 platforms with listen-link search.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {launchPack && Object.entries(launchPack.captions || {}).map(([platform, caption]) => (
              <div key={platform} className="rounded-md border border-line bg-bg-elevated p-3" data-testid={`launch-caption-${platform}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">{platform}</div>
                  <button onClick={() => { navigator.clipboard.writeText(caption); toast.success("Caption copied"); }}
                          className="text-ink-muted hover:text-cyan-300 transition" data-testid={`launch-copy-${platform}`}>
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
                <p className="text-sm text-ink-primary whitespace-pre-wrap">{caption}</p>
              </div>
            ))}
            {launchPack?.listen_links && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <SubmitLink href={launchPack.listen_links.spotify_search} label="Find on Spotify" testid="launch-spotify-link" />
                <SubmitLink href={launchPack.listen_links.apple_search} label="Find on Apple Podcasts" testid="launch-apple-link" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Podcasts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = async () => {
    try {
      const res = await api.get("/podcasts");
      setItems(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/podcasts/${id}`);
      setItems(items.filter((i) => i.episode_id !== id));
      toast.success("Episode deleted");
      setRefreshTick((x) => x + 1);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="podcasts-page">
      <PageHeader
        eyebrow="// PODCAST VAULT"
        title="Publish to every podcast app on Earth."
        description="Upload audio, hit publish, and your RSS feed is ready for Spotify, Apple, YouTube, Amazon, iHeartRadio, Pandora and 6+ more apps — submit once each, episodes auto-distribute from there."
      />

      <PublishPanel stationId={user?.station_id} onRefresh={refreshTick} />

      {loading ? (
        <div className="text-center py-20 text-ink-muted font-mono text-xs">LOADING...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-ink-muted">
          <Mic2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">No episodes yet. Package one in the AI Studio.</div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="podcast-grid">
          {items.map((ep) => (
            <EpisodeCard key={ep.episode_id} ep={ep}
                         onChange={() => { load(); setRefreshTick((x) => x + 1); }}
                         onDelete={del} />
          ))}
        </div>
      )}
    </div>
  );
}

function Block({ title, body }) {
  return (
    <div className="rounded-lg border border-line bg-bg p-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">{title}</div>
      <p className="text-xs text-ink-secondary whitespace-pre-wrap leading-relaxed">{body}</p>
    </div>
  );
}
